"use client"

import { motion } from "framer-motion"
import { ArrowRight } from "lucide-react"
import { AppLink } from "@/components/navigation/app-link"
import { RosterPlayerCard } from "@/components/pages/roster/roster-player-card"
import { usePublishedRoster } from "@/lib/hooks/use-roster"

const PREVIEW_COUNT = 8

export function OurPlayers() {
  const { data: players = [], isLoading } = usePublishedRoster()
  const preview = players.slice(0, PREVIEW_COUNT)

  if (isLoading) {
    return (
      <section id="our-players" className="scroll-mt-24 bg-slate-50 py-20">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="flex justify-center py-10 text-sm text-slate-500">
            Loading players…
          </div>
        </div>
      </section>
    )
  }

  if (preview.length === 0) return null

  return (
    <section id="our-players" className="scroll-mt-24 bg-slate-50 py-20">
      <div className="container mx-auto max-w-7xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
            Our players
          </span>
          <h2 className="mt-3 text-3xl font-bold leading-tight tracking-tight text-slate-900 md:text-4xl">
            Players we represent
          </h2>
        </motion.div>

        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4">
          {preview.map((player, index) => (
            <motion.div
              key={player.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.04 }}
              className="h-full"
            >
              <RosterPlayerCard
                player={player}
                priority={index < 4}
                className="h-full"
              />
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-12 text-center"
        >
          <AppLink
            href="/players"
            className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:scale-[1.03]"
          >
            See all players
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </AppLink>
        </motion.div>
      </div>
    </section>
  )
}
